import { useEffect, useRef, useState } from 'react'
import { loadShareImageContent } from './content'
import type { ShareImageTarget } from './types'

interface PreparedShareImage {
	blob: Blob
	url: string
}

export default function useShareImageResource(target: ShareImageTarget) {
	const [preview, setPreview] = useState<{ url?: string; error?: boolean }>({})
	const resource = useRef<{
		controller: AbortController
		promise: Promise<PreparedShareImage>
		url?: string
	}>(undefined)

	const prepare = () => {
		if (resource.current) return resource.current.promise
		setPreview({})
		const controller = new AbortController()
		const generate = async () => {
			const [content, { default: generateShareImage }] = await Promise.all([
				loadShareImageContent({ target, signal: controller.signal }),
				import('./generateShareImage'),
			])
			controller.signal.throwIfAborted()
			const blob = await generateShareImage({ content, signal: controller.signal })
			controller.signal.throwIfAborted()
			const url = URL.createObjectURL(blob)
			if (resource.current) resource.current.url = url
			setPreview({ url })
			return { blob, url }
		}
		const promise = generate().catch((error) => {
			if (!controller.signal.aborted) {
				resource.current = undefined
				setPreview({ error: true })
			}
			throw error
		})
		resource.current = { controller, promise }
		return promise
	}

	useEffect(
		() => () => {
			resource.current?.controller.abort()
			if (resource.current?.url) URL.revokeObjectURL(resource.current.url)
		},
		[]
	)

	return { prepare, preview }
}
